import React, {useCallback, useMemo, useState} from "react";
import {useDropzone} from "react-dropzone";
import {useEntryStore} from "@pages/entry/stores/useEntryStore";
import {useEntryErrorStore} from "@pages/entry/stores/useEntryErrorStore";
import {EntryRepository} from "@pages/entry/modules/entry-repository";
import {getEntryVars} from "@pages/entry/modules/functions";

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const Attachment = () => {
    const files = useEntryStore((s) => s.files);
    const addFile = useEntryStore((s) => s.addFile);
    const removeFile = useEntryStore((s) => s.removeFile);
    const filesError = useEntryErrorStore((s) => s.files);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const repository = useMemo(() => new EntryRepository(getEntryVars()), []);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        setUploading(true);
        setUploadError("");

        try {
            const res = await repository.upload(acceptedFiles);
            if (res.STATUS === "OK" && res.uploaded) {
                res.uploaded.forEach((item) => addFile(item));
            } else {
                setUploadError(res.MESSAGE || "アップロードに失敗しました");
            }
        } catch (e) {
            setUploadError("アップロード中にエラーが発生しました");
        } finally {
            setUploading(false);
        }
    }, [repository, addFile]);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
        onDropRejected: (fileRejections) => {
            const messages = fileRejections.map((r) => {
                const errors = r.errors.map((e) =>
                    e.code === "file-too-large"
                        ? `${r.file.name} は200MBを超えています`
                        : e.message
                );
                return errors.join(", ");
            });
            setUploadError(messages.join("\n"));
        },
    });

    return (
        <div className="form-flex">
            <div className="form-flex-txt">添付ファイル<span>（200MBまで）</span></div>
            <div className="form-flex-input">
                <div
                    {...getRootProps()}
                    id="dropzone"
                    style={{
                        cursor: "pointer",
                        opacity: uploading ? 0.5 : 1,
                    }}
                    className={isDragActive ? "dropzone-active" : ""}
                >
                    <input {...getInputProps()} />
                    {uploading
                        ? "アップロード中..."
                        : isDragActive
                            ? "ここにドロップしてください"
                            : "概要書・面積表・平面図・立面図・断面図（矩形図）等ございましたらドロップ又は、アップロード願います。"
                    }
                </div>
                {uploadError && <p className="form-error-message">{uploadError}</p>}
                {filesError && <span className="form-error-message">{filesError}</span>}
                {files.length > 0 && (
                    <ul className="uploaded-files">
                        {files.map((file) => (
                            <li key={file.id} className="uploaded-item">
                                <span className="filename">- {file.name}</span>
                                <span className="filesize">（{formatFileSize(file.size)}）</span>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        removeFile(file.id);
                                    }}
                                    className="delete-link"
                                >
                                    削除
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
